import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["startInput", "endInput", "day", "startLabel", "endLabel", "monthScroller", "month"]

  static values = {
    minDate: String,
    blockedThingDates: { type: Array, default: [] },
    placementFullDates: { type: Array, default: [] },
    readonly: { type: Boolean, default: false }
  }

  connect() {
    this.choosingEnd = false
    this.boundExternalSync = this.syncState.bind(this)
    this.element.addEventListener("date-range-selector:sync", this.boundExternalSync)
    this.scheduleSyncAfterLayout()
  }

  disconnect() {
    this.element.removeEventListener("date-range-selector:sync", this.boundExternalSync)
  }

  // First paint can run while a parent (e.g. ad form calendar grid) still has zero height; re-sync after layout.
  scheduleSyncAfterLayout() {
    this.syncState()
    requestAnimationFrame(() => this.syncState())
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.syncState())
    })
  }

  selectDay(event) {
    if (this.readonlyValue) return

    const value = event.currentTarget.dataset.dateValue
    if (!value || this.beforeMinDate(value) || this.dayUnavailable(value)) return

    const start = this.startInputTarget.value
    const end = this.endInputTarget.value

    if (!start || !end) {
      this.setRange(value, value)
      this.choosingEnd = true
    } else if (this.choosingEnd && value >= start) {
      if (this.rangeHasUnavailableDay(start, value)) return
      this.setRange(start, value)
      this.choosingEnd = false
    } else {
      this.setRange(value, value)
      this.choosingEnd = true
    }
  }

  scrollBackward() {
    this.scrollMonths(-1)
  }

  scrollForward() {
    this.scrollMonths(1)
  }

  setRange(start, end) {
    this.startInputTarget.value = start
    this.endInputTarget.value = end
    this.dispatchInputChange(this.startInputTarget)
    this.dispatchInputChange(this.endInputTarget)
    this.syncState()
  }

  syncState() {
    const start = this.startInputTarget.value
    const end = this.endInputTarget.value

    this.dayTargets.forEach((button) => {
      const value = button.dataset.dateValue

      if (this.readonlyValue) {
        const inSelection = Boolean(start && end && value >= start && value <= end)
        button.disabled = true
        button.setAttribute("aria-pressed", inSelection ? "true" : "false")
        this.applyReadonlyDayClasses(button, inSelection)
        return
      }

      const beforeMin = this.beforeMinDate(value)
      const thing = this.dayBlockedByThing(value)
      const full = this.dayPlacementFull(value)
      const unavailable = thing || full || beforeMin

      button.disabled = unavailable

      const isStart = value === start
      const isEnd = value === end
      const isBetween = start && end && value > start && value < end

      button.setAttribute("aria-pressed", isStart || isEnd ? "true" : "false")
      this.applyDayClasses(button, { isStart, isEnd, isBetween, beforeMin, thing, full })

      const cell = button.parentElement
      const hint = cell?.querySelector(".date-range-selector-day-hint")
      const inSelection = Boolean(start && end && value >= start && value <= end)
      if (!cell) return
      if (!hint) {
        cell.classList.remove("group")
        return
      }
      // group-hover drives the tooltip; remove `group` only when the day is inside
      // the selected range (where no tooltip should appear). Thing-blocked and
      // placement-full days keep `group` so their hover hints remain visible.
      cell.classList.toggle("group", !inSelection)
    })

    this.startLabelTarget.textContent = start ? `Start: ${this.formatDate(start)}` : "Start: Select a start date"
    this.endLabelTarget.textContent = end ? `End: ${this.formatDate(end)}` : "End: Select an end date"
  }

  applyReadonlyDayClasses(button, inSelection) {
    button.classList.remove(
      "bg-gray-900",
      "bg-gray-200",
      "text-white",
      "text-gray-900",
      "text-gray-700",
      "hover:bg-gray-100",
      "cursor-not-allowed",
      "text-gray-300",
      "rounded-full",
      "rounded-md",
      "date-range-selector-day--thing",
      "date-range-selector-day--placement",
      "date-range-selector-day--both",
      "date-range-selector-day--locked-selection"
    )

    if (inSelection) {
      button.classList.add("rounded-full", "date-range-selector-day--locked-selection")
    } else {
      button.classList.add("cursor-not-allowed", "text-gray-300", "rounded-md")
    }
  }

  applyDayClasses(button, { isStart, isEnd, isBetween, beforeMin, thing, full }) {
    button.classList.remove(
      "bg-gray-900",
      "bg-gray-200",
      "text-white",
      "text-gray-900",
      "text-gray-700",
      "hover:bg-gray-100",
      "cursor-not-allowed",
      "text-gray-300",
      "rounded-full",
      "rounded-md",
      "date-range-selector-day--thing",
      "date-range-selector-day--placement",
      "date-range-selector-day--both",
      "date-range-selector-day--locked-selection"
    )

    if (beforeMin) {
      button.classList.add("cursor-not-allowed", "text-gray-300", "rounded-md")
      return
    }

    if (thing && full) {
      button.classList.add("cursor-not-allowed", "date-range-selector-day--both", "rounded-full")
      return
    }

    if (thing) {
      button.classList.add("cursor-not-allowed", "date-range-selector-day--thing", "rounded-full")
      return
    }

    if (full) {
      button.classList.add("cursor-not-allowed", "date-range-selector-day--placement", "rounded-full")
      return
    }

    button.classList.add("rounded-md")

    if (isStart || isEnd) {
      button.classList.add("bg-gray-900", "text-white")
    } else if (isBetween) {
      button.classList.add("bg-gray-200", "text-gray-900")
    } else {
      button.classList.add("text-gray-700", "hover:bg-gray-100")
    }
  }

  scrollMonths(direction) {
    const distance = this.monthScrollDistance() * direction
    this.monthScrollerTarget.scrollBy({ left: distance, behavior: "smooth" })
  }

  monthScrollDistance() {
    if (this.monthTargets.length > 1) {
      return this.monthTargets[1].offsetLeft - this.monthTargets[0].offsetLeft
    }

    return this.monthTargets[0]?.offsetWidth || this.monthScrollerTarget.clientWidth
  }

  beforeMinDate(value) {
    return this.hasMinDateValue && value < this.minDateValue
  }

  dayUnavailable(value) {
    return this.dayBlockedByThing(value) || this.dayPlacementFull(value)
  }

  dayBlockedByThing(value) {
    return this.blockedThingDatesValue.includes(value)
  }

  dayPlacementFull(value) {
    return this.placementFullDatesValue.includes(value)
  }

  rangeHasUnavailableDay(startStr, endStr) {
    const start = new Date(`${startStr}T12:00:00`)
    const end = new Date(`${endStr}T12:00:00`)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return true

    const cursor = new Date(start.getTime())
    while (cursor <= end) {
      if (this.dayUnavailable(this.toIsoDateLocal(cursor))) return true
      cursor.setDate(cursor.getDate() + 1)
    }
    return false
  }

  toIsoDateLocal(d) {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
  }

  dispatchInputChange(input) {
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  formatDate(value) {
    const date = new Date(`${value}T12:00:00`)
    if (Number.isNaN(date.getTime())) return value

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    }).format(date)
  }
}
